import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import Button from '../ui/Button';
import type { LaunchStep2Input } from '../../services/hrCycleLauncher';
import type { EPRSubmission } from '../../types/eprFramework';
import type { DirectoryEmployee } from '../../types/magicLink';
import { fetchDirectory } from '../../services/directoryService';

type RevieweeInput = LaunchStep2Input['reviewees'][number];
type Relationship = RevieweeInput['reviewers'][number]['relationship'];

const RELATIONSHIP_OPTIONS: { value: Relationship; label: string }[] = [
  { value: 'peer', label: 'Peer' },
  { value: 'manager', label: 'Manager' },
  { value: 'subordinate', label: 'Subordinate' },
];

export interface PanelBuilderProps {
  submissions: EPRSubmission[];
  value: RevieweeInput[];
  onChange: (next: RevieweeInput[]) => void;
}

const PanelBuilder: React.FC<PanelBuilderProps> = ({ submissions, value, onChange }) => {
  const [directory, setDirectory] = useState<DirectoryEmployee[]>([]);
  const [showJson, setShowJson] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const initialized = useRef(false);

  // Load directory
  useEffect(() => {
    let alive = true;
    void fetchDirectory().then(r => { if (alive) setDirectory(r); }).catch(() => undefined);
    return () => { alive = false; };
  }, []);

  const directoryById = useMemo(() => {
    const map = new Map<string, DirectoryEmployee>();
    directory.forEach(d => map.set(d.id, d));
    return map;
  }, [directory]);

  // Auto-initialize value from submissions if empty (only ONCE)
  useEffect(() => {
    if (!initialized.current && submissions.length > 0 && directory.length > 0) {
      initialized.current = true;
      if (value.length === 0) {
        const initial: RevieweeInput[] = submissions.map(s => {
          const dirEmp = directoryById.get(s.employeeId);
          return {
            revieweeId: s.employeeId,
            revieweeName: dirEmp?.name || s.employeeId,
            reviewers: [],
          };
        });
        onChange(initial);
      }
    }
  }, [submissions, directory, value.length, onChange, directoryById]);

  // Make sure we have a map for fast lookup of current states
  const valueMap = useMemo(() => {
    const map = new Map<string, RevieweeInput>();
    value.forEach(v => map.set(v.revieweeId, v));
    return map;
  }, [value]);

  const toggleReviewee = (submission: EPRSubmission) => {
    if (valueMap.has(submission.employeeId)) {
      onChange(value.filter(v => v.revieweeId !== submission.employeeId));
    } else {
      const dirEmp = directoryById.get(submission.employeeId);
      onChange([...value, {
        revieweeId: submission.employeeId,
        revieweeName: dirEmp?.name || submission.employeeId,
        reviewers: [],
      }]);
    }
  };

  const addReviewer = (revieweeId: string, de: DirectoryEmployee) => {
    const current = valueMap.get(revieweeId);
    if (!current) return;
    
    // Prevent duplicates
    if (current.reviewers.some(r => r.reviewerId === de.id)) return;

    // Default relationship to peer, or manager if it matches the supervisor
    const isManager = submissions.find(s => s.employeeId === revieweeId)?.supervisorId === de.id;

    const nextReviewer = {
      reviewerId: de.id,
      reviewerEmail: de.email,
      reviewerName: de.name,
      relationship: (isManager ? 'manager' : 'peer') as Relationship,
    };

    const nextValue = value.map(v => {
      if (v.revieweeId === revieweeId) {
        return { ...v, reviewers: [...v.reviewers, nextReviewer] };
      }
      return v;
    });
    onChange(nextValue);
  };

  const removeReviewer = (revieweeId: string, reviewerId: string) => {
    const nextValue = value.map(v => {
      if (v.revieweeId === revieweeId) {
        return { ...v, reviewers: v.reviewers.filter(r => r.reviewerId !== reviewerId) };
      }
      return v;
    });
    onChange(nextValue);
  };

  const changeRelationship = (revieweeId: string, reviewerId: string, rel: Relationship) => {
    const nextValue = value.map(v => {
      if (v.revieweeId === revieweeId) {
        return {
          ...v,
          reviewers: v.reviewers.map(r => r.reviewerId === reviewerId ? { ...r, relationship: rel } : r)
        };
      }
      return v;
    });
    onChange(nextValue);
  };

  const applyJson = useCallback(() => {
    try {
      const parsed = JSON.parse(jsonText) as RevieweeInput[];
      if (!Array.isArray(parsed)) throw new Error('Must be an array');
      onChange(parsed);
      setShowJson(false);
      setJsonText('');
    } catch (e) {
      alert(`Invalid JSON: ${(e as Error).message}`);
    }
  }, [jsonText, onChange]);

  if (submissions.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-stone-300 bg-stone-50 p-6 text-center text-sm text-stone-500">
        No submissions available. Launch Step 1 first.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-2">
        <p className="text-sm text-stone-600">
          Select reviewees and assign their 360 reviewers. Uncheck a reviewee if they don't need a 360 panel.
        </p>
        <button
          type="button"
          onClick={() => setShowJson(s => !s)}
          className="text-xs text-stone-500 hover:text-stone-800 underline"
        >
          {showJson ? 'Hide JSON' : 'Import JSON'}
        </button>
      </div>

      {showJson && (
        <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 space-y-2 mb-4">
          <textarea
            value={jsonText}
            onChange={e => setJsonText(e.target.value)}
            rows={6}
            className="w-full rounded border border-stone-300 bg-white px-2 py-1 text-xs font-mono"
            placeholder='[{ "revieweeId": "...", "reviewers": [...] }]'
          />
          <div className="flex justify-end gap-2">
            <Button variant="tertiary" onClick={() => setShowJson(false)}>Cancel</Button>
            <Button variant="secondary" onClick={applyJson}>Apply</Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {submissions.map(sub => {
          const isSelected = valueMap.has(sub.employeeId);
          const revieweeData = valueMap.get(sub.employeeId);
          const dirEmp = directoryById.get(sub.employeeId);
          const name = dirEmp?.name || sub.employeeId;
          const email = dirEmp?.email || 'No email';

          return (
            <div 
              key={sub.id} 
              className={`rounded-lg border transition-colors ${isSelected ? 'border-emerald-500/50 bg-white shadow-sm' : 'border-stone-200 bg-stone-50 opacity-75'}`}
            >
              <div className="flex items-center gap-3 p-3 border-b border-stone-100 last:border-0">
                <input 
                  type="checkbox" 
                  checked={isSelected}
                  onChange={() => toggleReviewee(sub)}
                  className="w-4 h-4 text-emerald-600 rounded border-stone-300 focus:ring-emerald-500 cursor-pointer accent-emerald-600"
                />
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => toggleReviewee(sub)}>
                  <div className="text-sm font-medium text-stone-900 truncate">{name}</div>
                  <div className="text-xs text-stone-500 truncate">{email}</div>
                </div>
                
                {isSelected && (
                  <div className="w-64">
                    <DirectoryPicker 
                      directory={directory} 
                      placeholder="Add reviewer..." 
                      onPick={(de) => addReviewer(sub.employeeId, de)}
                      excludeId={sub.employeeId}
                    />
                  </div>
                )}
              </div>

              {isSelected && revieweeData && revieweeData.reviewers.length > 0 && (
                <div className="p-3 bg-stone-50/50 border-t border-stone-100 flex flex-wrap gap-2">
                  {revieweeData.reviewers.map(reviewer => (
                    <div key={reviewer.reviewerId} className="inline-flex items-center gap-1.5 bg-white border border-stone-200 rounded-full px-2 py-1 text-xs shadow-sm">
                      <select 
                        value={reviewer.relationship}
                        onChange={(e) => changeRelationship(sub.employeeId, reviewer.reviewerId, e.target.value as Relationship)}
                        className="bg-transparent text-stone-500 font-medium cursor-pointer outline-none hover:text-stone-800"
                      >
                        {RELATIONSHIP_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      <div className="w-px h-3 bg-stone-300"></div>
                      <span className="font-medium text-stone-800 truncate max-w-[120px]" title={reviewer.reviewerName || reviewer.reviewerEmail}>
                        {(reviewer.reviewerName || reviewer.reviewerEmail).split(' ')[0]}
                      </span>
                      <button 
                        onClick={() => removeReviewer(sub.employeeId, reviewer.reviewerId)}
                        className="ml-1 w-4 h-4 rounded-full hover:bg-red-100 text-stone-400 hover:text-red-600 flex items-center justify-center transition-colors"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Extracted Mini Directory Picker ──────────────────────────────

const DirectoryPicker: React.FC<{
  directory: DirectoryEmployee[];
  placeholder?: string;
  onPick: (de: DirectoryEmployee) => void;
  excludeId?: string;
}> = ({ directory, placeholder, onPick, excludeId }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return []; // Don't show entire directory until typing
    return directory
      .filter(d => d.id !== excludeId)
      .filter(d => d.name.toLowerCase().includes(q) || d.email.toLowerCase().includes(q))
      .slice(0, 8);
  }, [directory, search, excludeId]);

  return (
    <div className="relative">
      <div className="relative flex items-center">
        <span className="absolute left-2.5 text-stone-400 material-symbols-rounded" style={{ fontSize: 16 }}>search</span>
        <input
          type="text"
          value={search}
          onChange={e => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 200)}
          placeholder={placeholder ?? 'Search…'}
          className="w-full rounded-lg border border-stone-300 bg-white pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
        />
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-10 right-0 mt-1 w-[280px] max-h-60 overflow-y-auto rounded-lg border border-stone-200 bg-white shadow-xl">
          {filtered.map(d => (
            <button
              key={d.id}
              type="button"
              onMouseDown={() => { onPick(d); setSearch(''); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-stone-100 flex items-center gap-3"
            >
              <div className="min-w-0">
                <div className="font-medium text-stone-900 truncate">{d.name}</div>
                <div className="text-xs text-stone-500 truncate">{d.email}</div>
              </div>
            </button>
          ))}
        </div>
      )}
      {open && search.length > 0 && filtered.length === 0 && (
        <div className="absolute z-10 right-0 mt-1 w-[280px] rounded-lg border border-stone-200 bg-white px-3 py-2 text-xs text-stone-500 shadow-lg">
          No match found.
        </div>
      )}
    </div>
  );
};

export default PanelBuilder;
